import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, ZoomInIcon } from 'lucide-react';
import { pageTypes } from '../data/baseball';
import { pageSamples } from '../data/pageSamples';
const EASE = [0.23, 1, 0.32, 1] as const;
const arrowClass = 'absolute top-1/2 z-10 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border-4 border-white bg-red-700 text-white shadow-lg shadow-red-700/40 transition-[transform,background-color,opacity] duration-150 ease-out hover:bg-red-800 active:scale-95 focus:outline-none focus-visible:ring-4 focus-visible:ring-red-700/40 disabled:pointer-events-none disabled:opacity-0 sm:h-16 sm:w-16';
function pad(value: number): string {
  return value.toString().padStart(2, '0');
}
export function InsidePages() {
  const total = pageSamples.length;
  const [[index, direction], setState] = useState<[number, number]>([0, 0]);
  const draggedRef = useRef<boolean>(false);
  const goTo = useCallback((next: number) => {
    setState(([current]) => {
      const clamped = Math.max(0, Math.min(total - 1, next));
      return [clamped, clamped > current ? 1 : -1];
    });
  }, [total]);

  // Preload neighbouring pages so switching feels instant.
  useEffect(() => {
    [index - 1, index + 1].forEach(i => {
      if (pageSamples[i]) {
        const img = new Image();
        img.src = pageSamples[i].src;
      }
    });
  }, [index]);
  if (total === 0) return null;
  const page = pageSamples[index];
  return <section id="inside-pages" className="w-full scroll-mt-12 border-y border-slate-900/10 bg-stone-100 px-4 py-16 sm:px-10 sm:py-24" data-id="element-188" data-mp="uv6hauq4asqh">
      <div className="mx-auto max-w-5xl" data-id="element-189" data-mp="yxtcn5lzyvsf">
        <div className="mx-auto max-w-2xl text-center" data-id="element-190" data-mp="yyuq1gqhjudb">
          <p className="font-serif text-2xl italic leading-none text-red-700" data-id="element-191" data-mp="2pa91ccwedim0">Look inside Volume 1</p>
          <h2 className="mt-3 font-serif text-[2.3rem] leading-[1.02] tracking-tight text-slate-900 sm:text-6xl" data-id="element-192" data-mp="2t9rntd3va1qy">
            Real pages <em className="text-red-700" data-id="element-193" data-mp="63n3xuobf2zc">you can read.</em>
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-slate-900/75" data-id="element-194" data-mp="127gjvyo0kds">
            Use the arrows to flip through the pages exactly as they appear in Volume 1.
          </p>
        </div>

        <div role="group" aria-roledescription="carousel" aria-label="Real pages from Volume 1" tabIndex={0} onKeyDown={event => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          goTo(index + 1);
        }
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          goTo(index - 1);
        }
      }} className="relative mx-auto mt-10 w-full max-w-[720px] focus:outline-none sm:mt-14 sm:px-20" data-id="element-195" data-mp="3o8qfny8sed8q">
          <button type="button" onClick={() => goTo(index - 1)} disabled={index === 0} aria-label="Previous page" className={`${arrowClass} left-2 sm:left-0`} data-id="element-196" data-mp="azlw7tcrl551">
            <ChevronLeftIcon className="h-6 w-6 sm:h-8 sm:w-8" strokeWidth={2.75} aria-hidden="true" data-id="element-197" data-mp="13oj555v35pl4" />
          </button>

          <div className="relative aspect-[4/5] w-full overflow-hidden rounded-lg border border-slate-900/10 bg-white shadow-2xl shadow-slate-900/20" data-id="element-198" data-mp="1b3d0pla02qkh">
            <AnimatePresence initial={false} custom={direction} mode="popLayout" data-id="element-199" data-mp="1kz6ipk6hnc9n">
              <motion.a key={page.src} href={page.src} target="_blank" rel="noopener noreferrer" aria-label={`Open ${page.label} full size`} custom={direction} variants={{
              enter: (d: number) => ({
                x: d >= 0 ? '12%' : '-12%',
                opacity: 0
              }),
              center: {
                x: 0,
                opacity: 1
              },
              exit: (d: number) => ({
                x: d >= 0 ? '-12%' : '12%',
                opacity: 0
              })
            }} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.25,
              ease: EASE
            }} drag="x" dragConstraints={{
              left: 0,
              right: 0
            }} dragElastic={0.2} onDragStart={() => {
              draggedRef.current = true;
            }} onDragEnd={(_, info) => {
              if (info.offset.x < -60) goTo(index + 1);
              if (info.offset.x > 60) goTo(index - 1);
            }} onClickCapture={event => {
              // Prevent opening the image after a swipe.
              if (draggedRef.current) {
                event.preventDefault();
                draggedRef.current = false;
              }
            }} className="absolute inset-0 block" data-id="element-200" data-mp="1p1jdjdjp5prn">
                <img src={page.src} alt={`${page.label} – ${page.title}`} draggable={false} className="block h-full w-full select-none object-contain" data-id="element-201" data-mp="1h4vgn859t7dp" />
                <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-slate-900/80 px-3 py-1.5 text-xs font-semibold text-white" data-id="element-202" data-mp="1q25tgxxql4kq">
                  <ZoomInIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-203" data-mp="31im7cmtn7d5h" />
                  Full size
                </span>
              </motion.a>
            </AnimatePresence>
          </div>

          <button type="button" onClick={() => goTo(index + 1)} disabled={index === total - 1} aria-label="Next page" className={`${arrowClass} right-2 sm:right-0`} data-id="element-204" data-mp="s9fw3eu2tlvl">
            <ChevronRightIcon className="h-6 w-6 sm:h-8 sm:w-8" strokeWidth={2.75} aria-hidden="true" data-id="element-205" data-mp="ebznpxs7zi17" />
          </button>
        </div>

        <div className="mx-auto mt-6 max-w-[560px] text-center" aria-live="polite" data-id="element-206" data-mp="16yu2ce2cvn6r">
          <p className="font-serif text-xl tabular-nums text-slate-900" data-id="element-207" data-mp="1b05x0y036ylt">
            {pad(index + 1)}
            <span className="text-slate-900/40" data-id="element-208" data-mp="x8sjq1sruwz8"> / {pad(total)}</span>
          </p>
          <p className="mt-3 text-sm font-medium text-red-700" data-id="element-209" data-mp="x6y5t7fx454n">{page.label}</p>
          <h3 className="mt-1 font-serif text-2xl leading-tight text-slate-900 sm:text-3xl" data-id="element-210" data-mp="12611i1a0cyzy">{page.title}</h3>
          <p className="mt-2 leading-relaxed text-slate-900/70" data-id="element-211" data-mp="1pvd6eoyaykwg">{page.text}</p>
        </div>

        <div className="mt-6 flex justify-center gap-1.5" data-id="element-212" data-mp="1uphg3fyyd718">
          {pageSamples.map((sample, dotIndex) => <button key={sample.src} type="button" onClick={() => goTo(dotIndex)} aria-label={`Go to page ${dotIndex + 1}`} aria-current={dotIndex === index} className="flex h-6 items-center px-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-700/40" data-id="element-213" data-mp="24jdi9f02fe5k" data-mpi={dotIndex}>
              <span className={`block h-1.5 w-5 rounded-full transition-colors duration-150 ease-out ${dotIndex === index ? 'bg-slate-900' : 'bg-slate-900/20'}`} data-id="element-214" data-mp="1pluotzcg0crs" />
            </button>)}
        </div>

        <p className="mx-auto mt-8 max-w-3xl border-t border-slate-900/15 pt-5 text-center text-sm text-slate-900/70" data-id="element-215" data-mp="1gtj6z1ve9xnf">
          {pageTypes.join(' · ')}
        </p>
      </div>
    </section>;
}